import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, Box, Button, Dialog, Field, Grid, HStack, Input, Text, VStack } from '@chakra-ui/react';
import { PageContainer } from '../components/shared/PageContainer'
import { ResponsiveModal } from '../components/shared/ResponsiveModal'
import { TemplatePicker } from '../components/communications/TemplatePicker'
import { MergeFieldInsertEditor } from '../components/communications/MergeFieldInsertEditor'
import { RecipientPreviewCard } from '../components/communications/RecipientPreviewCard'
import { MergeFieldLegend } from '../components/communications/MergeFieldLegend'
import { EmailPreviewPane } from '../components/communications/EmailPreviewPane'
import { useAuth } from '../hooks/useAuth'
import { useCommsStatus, useRecipientPreview, useSaveTemplate, useSendEmail } from '../hooks/useCommunications'
import { useReadCaps } from '../hooks/useLimits'
import { extractApiMessage } from '../lib/apiError'
import { useReturnTo } from '../lib/returnNav'
import { toaster } from '../components/ui/toaster'
import type { AttendeeDeliveryMode } from '../types/lobbyDay'
import type { CreateEmailResult, EmailSourceKind, EmailSourceRef } from '../types/communications'

// URL uses the lowerCamel spelling the entry buttons pass (spec §8:
// ?source=lobbyDayEvent|contactList&id=…); the wire/type layer uses the PascalCase enum member
// names verbatim (matches the backend's JsonStringEnumConverter, per types/communications.ts).
const SOURCE_KIND_BY_PARAM: Record<string, EmailSourceKind> = {
  lobbyDayEvent: 'LobbyDayEvent',
  contactList: 'ContactList',
}

function sourceKindLabel(kind: EmailSourceKind): string {
  return kind === 'LobbyDayEvent' ? 'Lobby Day event' : 'Contact list'
}

export function EmailComposePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const back = useReturnTo()
  const { user } = useAuth()

  const sourceParam = searchParams.get('source')
  const sourceId = searchParams.get('id')
  const sourceKind = sourceParam ? SOURCE_KIND_BY_PARAM[sourceParam] : undefined

  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [replyTo, setReplyTo] = useState(user?.email ?? '')
  const [replyToTouched, setReplyToTouched] = useState(false)
  const [deliveryModes, setDeliveryModes] = useState<AttendeeDeliveryMode[]>([])
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  const [templateName, setTemplateName] = useState('')

  // Only carries deliveryModes when non-empty -- an empty array is "no filter, all modes" per
  // the api layer's normalizeSourceRef (task-9 fix round 1), so there is no need to special-case
  // an empty selection here too.
  const source: EmailSourceRef | null = useMemo(() => {
    if (!sourceKind || !sourceId) return null
    return {
      kind: sourceKind,
      id: sourceId,
      ...(sourceKind === 'LobbyDayEvent' && deliveryModes.length > 0 ? { deliveryModes } : {}),
    }
  }, [sourceKind, sourceId, deliveryModes])

  const recipientPreview = useRecipientPreview(source, { enabled: !!source })
  const commsStatus = useCommsStatus()

  // Pre-fill Reply-to with the committee's configured default (falling back to the sender's own
  // email) once comms-status resolves -- but never clobber a value the organizer has typed. The
  // field stays fully editable; this only sets the initial default (LW committee-reply-to).
  const committeeDefaultReplyTo = commsStatus.data?.defaultReplyTo
  useEffect(() => {
    if (replyToTouched) return
    setReplyTo(committeeDefaultReplyTo || user?.email || '')
  }, [committeeDefaultReplyTo, replyToTouched, user?.email])
  const sendEmail = useSendEmail()
  const saveTemplate = useSaveTemplate()

  const willReceive = recipientPreview.data?.willReceive ?? 0
  const caps = useReadCaps()
  // Pre-flight only: the server cap is the gate. Unknown caps => no warning, submit unchanged.
  const overRecipientCap = caps !== undefined && willReceive > caps.maxEmailRecipients
  const providerConfigured = commsStatus.data?.providerConfigured !== false
  const sendDisabled =
    !source ||
    !subject.trim() ||
    !body.trim() ||
    willReceive === 0 ||
    !providerConfigured ||
    sendEmail.isPending

  const handleSelectTemplate = (tSubject: string, tBody: string) => {
    setSubject(tSubject)
    setBody(tBody)
  }

  const handleConfirmSend = () => {
    if (!source) return
    sendEmail.mutate(
      { subject, bodyMarkdown: body, replyTo: replyTo.trim() || null, source },
      {
        onSuccess: (result: CreateEmailResult) => {
          setConfirmOpen(false)
          // Route is registered nested under /dashboard (App.tsx) -- an absolute navigate()
          // without that prefix would have missed every route and landed nowhere now that
          // Task 11 actually registers communications/email/:id.
          const returnTo = searchParams.get('returnTo')
          const returnLabel = searchParams.get('returnLabel')
          const suffix = returnTo
            ? `?returnTo=${encodeURIComponent(returnTo)}${returnLabel ? `&returnLabel=${encodeURIComponent(returnLabel)}` : ''}`
            : ''
          navigate(`/dashboard/communications/email/${result.messageId}${suffix}`)
        },
      },
    )
  }

  const handleSaveTemplate = () => {
    if (!templateName.trim()) return
    saveTemplate.mutate(
      { name: templateName.trim(), subject, bodyMarkdown: body },
      {
        onSuccess: () => {
          toaster.create({ title: 'Template saved', type: 'success', duration: 4000 })
          setTemplateDialogOpen(false)
          setTemplateName('')
        },
      },
    )
  }

  if (!sourceKind || !sourceId) {
    return (
      <PageContainer title="Compose email" back={back}>
        <Alert.Root status="error" borderRadius="md">
          <Alert.Indicator />
          <Alert.Content>
            Missing recipient source. Open this page from a Lobby Day event or a contact list.
          </Alert.Content>
        </Alert.Root>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="Compose email" subtitle={`Sending to: ${sourceKindLabel(sourceKind)}`} back={back}>
      {!providerConfigured && (
        <Alert.Root status="warning" borderRadius="md" mb={4}>
          <Alert.Indicator />
          <Alert.Content>
            Email sending is not configured for this committee yet. Contact your administrator.
          </Alert.Content>
        </Alert.Root>
      )}

      {!!sendEmail.error && (
        <Alert.Root status="error" borderRadius="md" mb={4}>
          <Alert.Indicator />
          <Alert.Content>{extractApiMessage(sendEmail.error, 'Could not send this email.')}</Alert.Content>
        </Alert.Root>
      )}

      <Grid templateColumns={{ base: '1fr', md: '2fr 1fr' }} gap={6} pb={24}>
        <VStack align="stretch" gap={4}>
          <HStack>
            <TemplatePicker onSelect={handleSelectTemplate} />
          </HStack>
          <MergeFieldInsertEditor
            source={sourceKind}
            subject={subject}
            onSubjectChange={setSubject}
            body={body}
            onBodyChange={setBody}
          />
          <Field.Root>
            <Field.Label fontSize="sm" color="fg.muted">Reply-to</Field.Label>
            <Input
              type="email"
              value={replyTo}
              onChange={(e) => {
                setReplyToTouched(true)
                setReplyTo(e.target.value)
              }}
              placeholder="reply-to@example.org"
            />
          </Field.Root>
        </VStack>

        <VStack align="stretch" gap={4}>
          <RecipientPreviewCard
            sourceKind={sourceKind}
            deliveryModes={deliveryModes}
            onDeliveryModesChange={setDeliveryModes}
            preview={recipientPreview.data}
            isLoading={recipientPreview.isLoading}
            error={recipientPreview.error}
          />
          <MergeFieldLegend source={sourceKind} />
          <EmailPreviewPane subject={subject} bodyMarkdown={body} source={source} />
        </VStack>
      </Grid>

      <Box
        position="sticky"
        bottom={0}
        bg="surface"
        borderTop="1px"
        borderColor="border"
        px={{ base: 4, md: 6 }}
        pt={3}
        pb={{ base: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))', md: 3 }}
      >
        <HStack justify="flex-end" gap={3}>
          <Button
            variant="outline"
            onClick={() => setTemplateDialogOpen(true)}
            disabled={!subject.trim() && !body.trim()}
          >
            Save as template
          </Button>
          <Button
            colorPalette="brand"
            onClick={() => setConfirmOpen(true)}
            disabled={sendDisabled}
            loading={sendEmail.isPending}
          >
            Send to {willReceive}
          </Button>
        </HStack>
      </Box>

      <ResponsiveModal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content role="alertdialog">
            <Dialog.Header fontSize="lg" fontWeight="bold">Send email</Dialog.Header>
            <Dialog.Body>
              <Text>
                Sends {willReceive} email{willReceive === 1 ? '' : 's'} now; this can't be recalled.
              </Text>
              {overRecipientCap && (
                <Text color="warning.fg" fontSize="sm" mt={2}>
                  That is {willReceive.toLocaleString()} recipients; this server sends at most{' '}
                  {caps!.maxEmailRecipients.toLocaleString()} in one message — narrow the source, or
                  the send will be refused.
                </Text>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Button onClick={() => setConfirmOpen(false)} disabled={sendEmail.isPending}>Cancel</Button>
              <Button
                colorPalette="brand"
                ml={3}
                onClick={handleConfirmSend}
                loading={sendEmail.isPending}
              >
                Send to {willReceive}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>

      <ResponsiveModal isOpen={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header fontSize="lg" fontWeight="bold">Save as template</Dialog.Header>
            <Dialog.Body>
              <Field.Root required>
                <Field.Label fontSize="sm" color="fg.muted">Template name</Field.Label>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g. Lobby Day reminder"
                />
              </Field.Root>
              {!!saveTemplate.error && (
                <Alert.Root status="error" borderRadius="md" mt={3}>
                  <Alert.Indicator />
                  <Alert.Content>{extractApiMessage(saveTemplate.error, 'Could not save this template.')}</Alert.Content>
                </Alert.Root>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Button onClick={() => setTemplateDialogOpen(false)} disabled={saveTemplate.isPending}>Cancel</Button>
              <Button
                colorPalette="brand"
                ml={3}
                onClick={handleSaveTemplate}
                disabled={!templateName.trim()}
                loading={saveTemplate.isPending}
              >
                Save
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>
    </PageContainer>
  );
}
